import {groupByUniqueKey} from '@benchling/js-utils';
import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';
import {NucleotideType} from 'biojs/BioConstants';
import {digestableEnzyme} from 'biojs/Util';
import {Strand} from 'constants/MolbioConstants';
import {SequenceType} from 'constants/SequenceConstants';
import * as browser from 'lib/Browser';
import {search, SequenceMatch} from 'molbio/biological-search/Search';

import EnzymeConstants, {
  BaseProvider,
  Cut,
  DigestableEnzyme,
  EnzymeFromJSON,
  EnzymeList,
  Provider,
  ProviderList,
} from './EnzymeConstants';

// Only accessed through getEnzyme (so that it can be stubbed)
let enzymeLookup: {[enzymeId: number]: DigestableEnzyme} = {};
export const getEnzyme = (enzymeId: number): DigestableEnzyme => enzymeLookup[enzymeId];

export const getProviderList = (providerListId: string | number): ProviderList | undefined =>
  EnzymeConstants.providerLists.find(({id}) => id === providerListId);

function _findProvider<T>(
  name: string,
  func: (provider: Provider, baseProvider: BaseProvider) => T
): T | null {
  for (const baseProvider of EnzymeConstants.baseProviders) {
    for (const provider of [baseProvider, baseProvider.subProvider]) {
      if (provider?.name === name) {
        return func(provider, baseProvider);
      }
    }
  }
  return null;
}

export const getBaseProvider = (name: string): BaseProvider =>
  _findProvider<BaseProvider>(name, (_, baseProvider) => baseProvider)!;

export const getProvider = (name: string): Provider =>
  _findProvider<Provider>(name, (provider, _) => provider)!;

export const getCompatibleEnzymeIds = (enzymeId: number): Array<number> => getEnzyme(enzymeId).compatible;

// Exported only for testing purposes
export function _getActivity(enzyme: EnzymeFromJSON, providerName: string, buffer: string): number {
  const activity = enzyme.providedEnzymes[providerName]?.activities[buffer];
  const match = activity == null ? null : /^\d+(.)?\d*/.exec(activity);
  if (activity == null || match == null) {
    return -Infinity;
  }
  return parseFloat(match[0]);
}

/**
 * Calculates the optimal buffers of the given provider for the given set of enzymes, where optimal is
 * defined as having the maximal sum of buffer activities across all enzymes, excluding any buffers that
 * do not have data for all of the enzymes or an activity is less than 50%.
 *
 * @param  {Array} enzymeIds     an array of enzyme ids corresponding to the enzymes of interest
 * @param  {String} providerName the name of the desired provider
 * @return {Array}               an array of the optimal buffer names as strings
 */
export function calculateOptimalBuffers(enzymeIds: Array<number>, providerName: string): Array<string> {
  const enzymes = enzymeIds.map((enzymeId) => getEnzyme(enzymeId));
  const provider = getProvider(providerName);
  const bufferValues: {[name: string]: number} = _.object(
    provider.buffers
      .filter((buffer) => enzymes.every((enzyme) => _getActivity(enzyme, providerName, buffer) >= 50))
      .map((buffer) => [
        buffer,
        benchlingUtil.sum(enzymes.map((enzyme) => _getActivity(enzyme, providerName, buffer))),
      ])
  );

  const maxValue = Math.max(...Object.values(bufferValues));
  return provider.buffers.filter((buffer) => bufferValues[buffer] === maxValue);
}

export function getPreferredProviderForEnzyme(
  enzymeIds: Array<number>,
  desiredProviderName: string | null
): Provider {
  const enzymes = enzymeIds.map((enzymeId) => getEnzyme(enzymeId));
  if (desiredProviderName != null) {
    // If any enzyme has the desired provider, use it.
    const desiredProvider = getProvider(desiredProviderName);
    if (enzymes.some((enzyme) => Object.keys(enzyme.providedEnzymes).includes(desiredProviderName))) {
      return desiredProvider;
    }
    // Otherwise, try for the base provider.
    const desiredBaseProvider = getBaseProvider(desiredProviderName);
    if (enzymes.some((enzyme) => Object.keys(enzyme.providedEnzymes).includes(desiredBaseProvider.name))) {
      return desiredBaseProvider;
    }
  }
  // Otherwise, default to the first provider that is used by any enzyme.
  for (const baseProvider of EnzymeConstants.baseProviders) {
    for (const provider of [baseProvider, baseProvider.subProvider]) {
      if (
        provider != null &&
        enzymes.some((enzyme) => Object.keys(enzyme.providedEnzymes).includes(provider.name))
      ) {
        return provider;
      }
    }
  }
  throw new Error(`No viable provider found for enzymes: ${enzymeIds}`);
}

export function getProvidersForEnzymes(enzymeIds: Array<number>): Array<Provider> {
  const enzymes = enzymeIds.map((enzymeId) => getEnzyme(enzymeId));
  const providers = [];
  for (const baseProvider of EnzymeConstants.baseProviders) {
    for (const provider of [baseProvider, baseProvider.subProvider]) {
      if (
        provider != null &&
        enzymes.some((enzyme) => Object.keys(enzyme.providedEnzymes).includes(provider.name))
      ) {
        providers.push(provider);
      }
    }
  }
  return providers;
}

// Build enzymeLookupByList map that maps a provider list to the enzymes it contains, skipping over
// isoschizomers already present if multiple providers are used.
let enzymeLookupByList: {[id: string | number]: {[enzymeId: number]: boolean}} = {};

// Exported only for testing purposes
export function _buildLookup(enzymes: Array<EnzymeFromJSON>, enzymeLists: Array<EnzymeList>): void {
  enzymeLookup = groupByUniqueKey(
    enzymes.map((enzyme) => digestableEnzyme(enzyme)),
    (enzyme) => enzyme.id
  );
  enzymeLookupByList = {};
  // Create enzymeLookupByList mapping provider to a set of enzymes that it contains.
  const baseProviderToEnzymes: {[providerName: string]: {[enzymeId: string]: EnzymeFromJSON}} = {};
  for (const baseProvider of EnzymeConstants.baseProviders) {
    baseProviderToEnzymes[baseProvider.name] = {};
  }

  for (const enzyme of enzymes) {
    for (const providerName of Object.keys(enzyme.providedEnzymes)) {
      const baseProvider = getBaseProvider(providerName);
      baseProviderToEnzymes[baseProvider.name][enzyme.id] = enzyme;
    }
  }

  for (const providerList of EnzymeConstants.providerLists) {
    const providerRank: {[name: string]: number} = {};
    for (let i = 0; i < providerList.baseProviders.length; i++) {
      const baseProvider = providerList.baseProviders[i];
      for (const provider of [baseProvider, baseProvider.subProvider]) {
        if (provider != null) {
          providerRank[provider.name] = i;
        }
      }
    }
    const enzymeRank = (enzyme: EnzymeFromJSON): number => {
      const providerRanks = Object.keys(enzyme.providedEnzymes).map(
        (providerName) => providerRank[providerName]
      );
      return Math.min(...providerRanks);
    };
    const isLowestRankIso = (enzyme: EnzymeFromJSON): boolean =>
      !enzyme.isoschizomers
        .map((enzymeId) => getEnzyme(enzymeId))
        .some((isoEnzyme) => enzymeRank(isoEnzyme) < enzymeRank(enzyme));

    enzymeLookupByList[providerList.id] = {};
    for (const baseProvider of providerList.baseProviders) {
      const providerEnzymeLookup = baseProviderToEnzymes[baseProvider.name];
      for (const enzymeId of Object.keys(providerEnzymeLookup)) {
        // Include enzyme:
        // a. if the enzyme list allows duplicates, or
        // b. none if of its isoschizomers have a more preferred provider
        const enzyme = providerEnzymeLookup[enzymeId];
        if (!providerList.dedup || isLowestRankIso(enzyme)) {
          enzymeLookupByList[providerList.id][enzyme.id] = true;
        }
      }
    }
  }

  // Add user's custom enzyme lists to the lookup as well.
  for (const {id, members} of enzymeLists) {
    enzymeLookupByList[id] = {};
    for (const enzymeId of members) {
      enzymeLookupByList[id][enzymeId] = true;
    }
  }
}

const enzymeLists = browser.isBrowser ? window.bootstrap?.enzyme_lists : undefined;
_buildLookup(EnzymeConstants.enzymes, enzymeLists ?? []);

export const isEnzymeListId = (id: number): boolean => enzymeLookupByList[id] != null;
export const doesEnzymeListContain = (enzymeListId: number, enzymeId: number): boolean =>
  enzymeLookupByList[enzymeListId]?.[enzymeId] ?? false;

export function formatBufferName(name: string): string {
  if (name === 'MultiCore') {
    return 'MULTI';
  }
  return name;
}

export const isDoubleCutterEnzyme = (enzyme: EnzymeFromJSON): boolean => enzyme.cut2 != null;

export function getCuts(
  enzyme: DigestableEnzyme,
  sequence: string,
  nucleotideType: NucleotideType,
  sequenceType: SequenceType,
  circular: boolean,
  range: {start: number; end: number} | null = null
): Array<Cut> {
  // eslint-disable-next-line benchlint/no-in-operator
  if (!('matchingRegion' in enzyme)) {
    digestableEnzyme(enzyme);
  }

  const matches = search(enzyme.matchingRegion, sequence, nucleotideType, sequenceType, circular, {
    allowedChars: [],
    range,
    includeMatch: false,
  });
  const length = sequence.length;

  function cutFunc(m: SequenceMatch): {
    position: number;
    strand: Strand;
  } {
    let val;
    if (m.strand === 0) {
      val = m.index + enzyme.matchingCut;
    } else if (m.strand === 1) {
      val = m.index + enzyme.matchingCut;
    } else if (m.strand === -1) {
      val = m.index + enzyme.matchingRegion.length - enzyme.matchingCut - enzyme.offset;
    } else {
      throw new Error('Invalid strand');
    }
    return {position: (val + length) % length, strand: m.strand};
  }

  const ret = _.chain(matches)
    .map((match) => cutFunc(match))
    .sortBy((cut) => cut.position)
    .uniq(true, (cut) => cut.position)
    .value();

  return ret;
}

export function getEnzymeByName(enzymeName: string): EnzymeFromJSON | null {
  const possibleEnzymes = EnzymeConstants.enzymes.filter(
    (enzyme) => enzyme.name.toLowerCase() === enzymeName.trim().toLowerCase()
  );
  if (possibleEnzymes.length === 1) {
    return possibleEnzymes[0];
  }
  return null;
}
